/**
 * AssignDialog.jsx (iter142 refactor — extracted from CustomerPricing.jsx)
 *
 * Assigns a customer email to one of the 4 pricing tiers and lists every
 * customer currently on a tier. Parent supplies the tier list, the list of
 * already-assigned customers, and the PUT handler.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';

export const AssignDialog = ({ open, onClose, tiers, assignedList, onAssign }) => {
  const [email, setEmail] = useState('');
  const [tier, setTier] = useState('tier4');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!email.includes('@')) {
      toast.error('Enter a valid email.');
      return;
    }
    setBusy(true);
    try {
      await onAssign(email, tier);
      toast.success(`${email} assigned to ${tier}`);
      setEmail('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not assign');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="border border-border rounded-none max-w-2xl" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono">Assign customer to a pricing tier</DialogTitle>
          <DialogDescription className="text-xs">
            Pin one customer email to a specific tier. Their B2B portal will use the assigned tier&apos;s prices and overrides at checkout.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-[1fr_auto_auto] gap-2">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="customer@example.com"
              className="font-mono"
              data-testid="assign-email-input"
            />
            <select
              value={tier}
              onChange={(e) => setTier(e.target.value)}
              className="text-sm px-3 py-2 border border-border bg-background"
              data-testid="assign-tier-select"
            >
              {(tiers || []).map((t) => (
                <option key={t.key} value={t.key}>{t.name}</option>
              ))}
            </select>
            <Button
              onClick={submit}
              disabled={busy}
              className="bg-brand hover:bg-brand/90 text-white rounded-none"
              data-testid="assign-go-btn"
            >
              {busy ? '…' : 'Assign'}
            </Button>
          </div>
          <div className="border border-border max-h-[300px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase tracking-widest text-muted-foreground sticky top-0">
                <tr>
                  <th className="text-left p-2">Customer email</th>
                  <th className="text-left p-2">Tier</th>
                  <th className="text-left p-2">Updated</th>
                </tr>
              </thead>
              <tbody>
                {assignedList.length === 0 ? (
                  <tr><td colSpan={3} className="p-4 text-center text-muted-foreground text-xs">No customers assigned yet.</td></tr>
                ) : assignedList.map((row) => {
                  const t = tiers?.find((x) => x.key === row.tier_key);
                  return (
                    <tr key={row.customer_email} className="border-t border-border" data-testid={`assigned-${row.customer_email}`}>
                      <td className="p-2 font-mono text-xs">{row.customer_email}</td>
                      <td className="p-2"><span className="text-xs font-bold uppercase">{t?.name || row.tier_key}</span></td>
                      <td className="p-2 text-xs text-muted-foreground">{row.updated_at ? new Date(row.updated_at).toLocaleDateString('en-AU') : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => onClose(false)} className="rounded-none">Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
